import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Cpu } from "lucide-react";
import { IconType } from "react-icons";
import {
    SiReact,
    SiTypescript,
    SiNodedotjs,
    SiPython,
    SiRubyonrails,
    SiFlutter,
    SiTailwindcss,
    SiDocker,
    SiOpenai,
    SiNextdotjs,
    SiPostgresql,
    SiKubernetes,
} from "react-icons/si";
import Grid from "../Grid/grid";

const floatingIcons: {
    icon: IconType;
    color: string;
    top: string;
    left: string;
    delay: number;
}[] = [
        { icon: SiReact, color: "#61DAFB", top: "10%", left: "6%", delay: 0 },
        { icon: SiTypescript, color: "#3178C6", top: "12%", left: "88%", delay: 0.2 },
        { icon: SiRubyonrails, color: "#CC0000", top: "26%", left: "4%", delay: 0.8 },
        { icon: SiPostgresql, color: "#4169E1", top: "28%", left: "92%", delay: 2 },
        { icon: SiDocker, color: "#2496ED", top: "42%", left: "8%", delay: 1.4 },
        { icon: SiKubernetes, color: "#326CE5", top: "44%", left: "85%", delay: 2.2 },
        { icon: SiNodedotjs, color: "#339933", top: "58%", left: "5%", delay: 0.4 },
        { icon: SiFlutter, color: "#02569B", top: "60%", left: "91%", delay: 1 },
        { icon: SiNextdotjs, color: "#000000", top: "74%", left: "9%", delay: 1.8 },
        { icon: SiPython, color: "#3776AB", top: "76%", left: "87%", delay: 0.6 },
        { icon: SiTailwindcss, color: "#06B6D4", top: "88%", left: "3%", delay: 1.2 },
        { icon: SiOpenai, color: "#412991", top: "90%", left: "84%", delay: 1.6 },
    ];

function TechnologiesHeadSection({ onScroll }: { onScroll: () => void }) {
    return (
        <section className="w-full min-h-screen h-full relative flex flex-col overflow-hidden bg-gradient-to-br from-white via-primary/5 to-secondary/5">
            <Grid size={100} direction="full" />

            {/* Floating tech icons */}
            <div className="absolute inset-0 hidden md:block pointer-events-none">
                {floatingIcons.map((item, index) => (
                    <motion.div
                        key={index}
                        className="absolute text-4xl lg:text-5xl"
                        style={{
                            top: item.top,
                            left: item.left,
                            color: item.color,
                        }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: [0, 0.7, 0.4, 0.7],
                            scale: [0, 1, 0.95, 1],
                            y: [0, -15, 0, -15, 0],
                        }}
                        transition={{
                            duration: 6,
                            delay: item.delay,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    >
                        {React.createElement(item.icon as any)}
                    </motion.div>
                ))}
            </div>

            {/* Radial glow */}
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-white/60 to-white pointer-events-none" />

            <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-32 md:py-24 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 text-primary text-sm font-medium shadow-sm mb-6"
                >
                    <Cpu size={16} />
                    Our Technology Stack
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="text-4xl md:text-6xl lg:text-7xl font-light text-primary max-w-5xl leading-tight"
                >
                    The Technologies That{" "}
                    <span className="font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        Power Great Products
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mt-6 text-base md:text-xl text-gray-600 max-w-3xl font-light leading-relaxed"
                >
                    From lightning-fast frontends to AI-native backends, we build with the
                    modern stack that scales — carefully chosen for every project we ship.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="mt-10 flex flex-col sm:flex-row items-center gap-4"
                >
                    <button
                        onClick={onScroll}
                        className="group px-6 py-3 rounded-full bg-primary text-white flex items-center gap-2 hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                    >
                        <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                        Start Your Project
                    </button>
                    <a
                        href="#tech-stack"
                        className="px-6 py-3 rounded-full border-2 border-primary text-primary flex items-center gap-2 hover:bg-primary/5 transition-all"
                    >
                        Explore Our Stack
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 text-center"
                >
                    {[
                        { number: "40+", label: "Technologies Mastered" },
                        { number: "7", label: "Practice Areas" },
                        { number: "100%", label: "Modern Stacks" },
                        { number: "24/7", label: "Support & Monitoring" },
                    ].map((stat, i) => (
                        <div key={i}>
                            <div className="text-3xl md:text-4xl font-semibold text-primary">
                                {stat.number}
                            </div>
                            <div className="text-xs md:text-sm text-gray-500 mt-1 uppercase tracking-wide">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

export default TechnologiesHeadSection;
